// DateField.jsx — v140 📅 منتقي تاريخ احترافي (بديل input type="date").
// نفس واجهة الاستخدام: value="YYYY-MM-DD" و onChange({target:{value}}).
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { zVar } from "../lib/zIndex";
import { ksaToday } from "../lib/ksaTime";

const AR_MONTHS = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];
const AR_DAYS_FULL = ["الأحد", "الإثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];
const AR_DAYS = ["أحد", "إثن", "ثلا", "أرب", "خمي", "جمع", "سبت"];
const pad = (n) => String(n).padStart(2, "0");
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseISO = (s) => {
  if (!s) return null;
  const [y, m, d] = String(s).slice(0, 10).split("-").map(Number);
  return (y && m && d) ? new Date(y, m - 1, d) : null;
};

let styleInjected = false;
const injectStyle = () => {
  if (styleInjected) return; styleInjected = true;
  const el = document.createElement("style");
  el.textContent = `
    @keyframes twDfPop { from { opacity:0; transform:translateY(-6px) scale(.97);} to {opacity:1; transform:translateY(0) scale(1);} }
    .tw-df-pop { animation: twDfPop .16s ease-out; }
    .tw-df-day { transition: background-color .12s, color .12s, transform .08s; }
    .tw-df-day:not(:disabled):hover { background:#f0e9dc !important; }
    .tw-df-day:not(:disabled):active { transform:scale(.92); }
    .tw-df-trigger:hover { border-color:#c2a97f !important; }
    .tw-df-trigger:focus-visible { outline:2px solid #c2a97f; outline-offset:1px; }
  `;
  document.head.appendChild(el);
};

export default function DateField(props) {
  const { value, onChange, min, max, required, disabled, placeholder = "اختر التاريخ",
    style, className, type: _t, ...rest } = props;
  const testid = props["data-testid"];
  const selected = parseISO(value);
  // ⑤/L3-أ — «اليوم» بتوقيت السعودية، كمكوّنات يوم مجرّدة لشبكة التقويم.
  const today = (() => { const [y, m, d] = ksaToday().split("-").map(Number); return new Date(y, m - 1, d); })();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(() => {
    const d = selected || today;
    return { y: d.getFullYear(), m: d.getMonth() };
  });
  const [pos, setPos] = useState({ top: 0, left: 0, up: false });
  const trigRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => { injectStyle(); }, []);
  useEffect(() => {
    if (open) {
      const d = parseISO(value) || today;
      setView({ y: d.getFullYear(), m: d.getMonth() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const place = () => {
    const r = trigRef.current?.getBoundingClientRect();
    if (!r) return;
    const W = 300, H = 372;
    const up = r.bottom + H + 8 > window.innerHeight && r.top - H - 8 > 0;
    let left = Math.min(Math.max(8, r.right - W), window.innerWidth - W - 8);
    setPos({ top: up ? r.top - H - 6 : r.bottom + 6, left, up });
  };

  const openPop = () => {
    if (disabled) return;
    place();
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (popRef.current?.contains(e.target) || trigRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onScroll = (e) => { if (!popRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", place);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", place);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const minD = parseISO(min), maxD = parseISO(max);
  const inRange = (d) => (!minD || d >= minD) && (!maxD || d <= maxD);

  const grid = useMemo(() => {
    const first = new Date(view.y, view.m, 1);
    const daysIn = new Date(view.y, view.m + 1, 0).getDate();
    const lead = first.getDay(); // Sunday=0 first
    const cells = [];
    for (let i = 0; i < lead; i++) cells.push(null);
    for (let d = 1; d <= daysIn; d++) cells.push(new Date(view.y, view.m, d));
    return cells;
  }, [view]);

  const pick = (d) => {
    onChange?.({ target: { value: toISO(d) } });
    setOpen(false);
  };
  const nav = (delta) => setView((v) => {
    const d = new Date(v.y, v.m + delta, 1);
    return { y: d.getFullYear(), m: d.getMonth() };
  });

  const label = selected
    ? `${selected.getDate()} ${AR_MONTHS[selected.getMonth()]} ${selected.getFullYear()}`
    : placeholder;
  const years = useMemo(() => {
    const base = (selected || today).getFullYear();
    const lo = Math.min(base, today.getFullYear()) - 6, hi = Math.max(base, today.getFullYear()) + 4;
    const arr = []; for (let y = lo; y <= hi; y++) arr.push(y); return arr;
  }, [selected, today]);

  const isSame = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  return (
    <span style={{ position: "relative", display: "inline-block", width: style?.width || "100%" }} className={className}>
      <button type="button" ref={trigRef} onClick={openPop} disabled={disabled}
        className="tw-df-trigger" data-testid={testid} {...rest}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
          width: "100%", padding: "8px 10px", fontSize: 13, textAlign: "start",
          border: "1px solid var(--border, #e2d9c8)", borderRadius: 10,
          background: disabled ? "#f5f5f4" : "#fff", cursor: disabled ? "not-allowed" : "pointer",
          color: selected ? "var(--brown-deep, #3f2f23)" : "#a8a29e",
          fontFamily: "inherit", fontWeight: selected ? 700 : 400,
          ...style, width: "100%",
        }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {label}
          {selected && <span style={{ fontSize: 10.5, color: "#a8a29e", fontWeight: 400, marginInlineStart: 6 }}>({AR_DAYS_FULL[selected.getDay()]})</span>}
        </span>
        <span aria-hidden="true" style={{ fontSize: 14, flexShrink: 0, opacity: 0.75 }}>📅</span>
      </button>
      {required && (
        <input tabIndex={-1} aria-hidden="true" required value={value || ""} onChange={() => {}}
          style={{ position: "absolute", inset: "auto 0 0 0", height: 1, width: "100%", opacity: 0, pointerEvents: "none", border: 0 }} />
      )}
      {open && createPortal(
        <div ref={popRef} dir="rtl" className="tw-df-pop" data-testid={testid ? `${testid}-popover` : "datefield-popover"}
          style={{
            position: "fixed", top: pos.top, left: pos.left, zIndex: zVar('extra_2600'), width: 300,
            background: "#fffdf8", border: "1px solid #e8dfc9", borderRadius: 16,
            boxShadow: "0 18px 48px rgba(63,47,35,.22), 0 3px 10px rgba(63,47,35,.10)", padding: 12,
          }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, marginBottom: 8 }}>
            <button type="button" onClick={() => nav(-1)} data-testid="df-prev"
              style={navBtn}>›</button>
            <div style={{ display: "flex", gap: 6, flex: 1, justifyContent: "center" }}>
              <select value={view.m} onChange={(e) => setView((v) => ({ ...v, m: Number(e.target.value) }))}
                data-testid="df-month" style={selStyle}>
                {AR_MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
              </select>
              <select value={view.y} onChange={(e) => setView((v) => ({ ...v, y: Number(e.target.value) }))}
                data-testid="df-year" style={selStyle}>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <button type="button" onClick={() => nav(1)} data-testid="df-next"
              style={navBtn}>‹</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 2 }}>
            {AR_DAYS.map((d) => (
              <div key={d} style={{ textAlign: "center", fontSize: 10, fontWeight: 800, color: "#b0a58f", padding: "4px 0" }}>{d}</div>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
            {grid.map((d, i) => {
              if (!d) return <span key={`b${i}`} />;
              const sel = isSame(d, selected), tod = isSame(d, today), ok = inRange(d);
              return (
                <button key={i} type="button" disabled={!ok} onClick={() => pick(d)}
                  className="tw-df-day" data-testid={`df-day-${toISO(d)}`}
                  style={{
                    height: 34, borderRadius: 10, border: tod && !sel ? "1.5px solid #c2a97f" : "1px solid transparent",
                    background: sel ? "var(--brown-deep, #6B4E3D)" : "transparent",
                    color: sel ? "#fff" : ok ? "#3f2f23" : "#d6d3d1",
                    fontSize: 12.5, fontWeight: sel || tod ? 800 : 500, cursor: ok ? "pointer" : "not-allowed",
                    fontFamily: "inherit",
                  }}>
                  {d.getDate()}
                </button>
              );
            })}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, paddingTop: 10, borderTop: "1px solid #efe8d8" }}>
            <button type="button" data-testid="df-today"
              onClick={() => { if (inRange(today)) pick(today); }}
              style={{ ...footBtn, color: "#6B4E3D", fontWeight: 800 }}>⌂ اليوم</button>
            {!required && value && (
              <button type="button" data-testid="df-clear"
                onClick={() => { onChange?.({ target: { value: "" } }); setOpen(false); }}
                style={{ ...footBtn, color: "#b91c1c" }}>✕ مسح</button>
            )}
          </div>
        </div>,
        document.body
      )}
    </span>
  );
}

const navBtn = {
  width: 30, height: 30, borderRadius: 10, border: "1px solid #e8dfc9", background: "#fff",
  fontSize: 17, fontWeight: 900, color: "#6B4E3D", cursor: "pointer", lineHeight: 1, fontFamily: "inherit",
};
const selStyle = {
  fontSize: 12, fontWeight: 700, padding: "5px 6px", borderRadius: 8, border: "1px solid #e8dfc9",
  background: "#fff", color: "#3f2f23", cursor: "pointer", fontFamily: "inherit",
};
const footBtn = {
  border: "none", background: "transparent", fontSize: 12, cursor: "pointer", padding: "4px 8px",
  borderRadius: 8, fontFamily: "inherit",
};
